import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  IconButton,
  Spinner,
  SimpleGrid,
  Avatar,
  Textarea,
  Input,
  RadioGroup,
  Radio,
  Circle,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdArrowForward,
  MdCalendarToday,
  MdPerson,
  MdPhone,
  MdInfo,
  MdTimer,
  MdLocationOn,
  MdLocationCity,
  MdAddBox,
  MdExpandMore,
  MdLocalHospital,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../api/client";
import { BASE_URL } from "config/api";
import { getAllPatients } from "../../../api/api";

const StyledSearchDropdown = ({ value, onChange, options, placeholder, emptyText = "Aucun résultat." }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");

  const normalizedOptions = useMemo(
    () =>
      (options || []).map((opt) => ({
        value: String(opt?.value ?? ""),
        label: String(opt?.label ?? ""),
      })),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [normalizedOptions, searchQuery]);

  const selectedOption = normalizedOptions.find((opt) => String(opt.value) === String(value));

  return (
    <Box position="relative" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <Menu isOpen={isOpen} onClose={onClose} closeOnBlur={false}>
        <MenuButton
          as={Button}
          onClick={onOpen}
          w="100%"
          h="42px"
          justifyContent="flex-start"
          fontSize="13px"
          fontWeight="400"
          border="1px solid"
          borderRadius="6px"
          borderColor="#E5E5E5"
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          textAlign="left"
          px="12px"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>
        <MenuList
          p="10px"
          minW="100%"
          maxH="320px"
          overflowY="auto"
          borderRadius="10px"
          border="1px solid"
          borderColor="#E5E7EB"
          boxShadow="0px 12px 20px rgba(17,24,39,0.12)"
          zIndex={50}
        >
          <Box mb="10px">
            <Input
              size="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              h="36px"
              borderRadius="8px"
              border="1px solid"
              borderColor="#E5E7EB"
              bg="white"
            />
          </Box>

          {filteredOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              borderRadius="8px"
              fontSize="13px"
              onClick={() => {
                onChange(String(opt.value));
                onClose();
              }}
            >
              {opt.label}
            </MenuItem>
          ))}

          {filteredOptions.length === 0 ? (
            <Box px="10px" py="8px">
              <Text fontSize="13px" color="gray.500">
                {emptyText}
              </Text>
            </Box>
          ) : null}
        </MenuList>
      </Menu>
    </Box>
  );
};

const DoctorSearchDropdown = ({ value, onChange, options, placeholder }) => (
  <StyledSearchDropdown
    value={value}
    onChange={onChange}
    options={(options || []).map((m) => ({
      value: String(m.id),
      label: `${m.nom || ""} ${m.prenom || ""}`.trim() || `Médecin #${m.id}`,
    }))}
    placeholder={placeholder}
    emptyText="Aucun médecin trouvé."
  />
);

export default function DossierRegulationEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [dossier, setDossier] = useState(null);
  const [patientById, setPatientById] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientForm, setPatientForm] = useState({
    ipp: "",
    nom: "",
    prenom: "",
    age: "",
    sexe: "",
    téléphone: "",
    cin: "",
    adresse: "",
    ville: "",
  });
  const [usersById, setUsersById] = useState({});
  const [hospitals, setHospitals] = useState([]);
  const [hospitalVilles, setHospitalVilles] = useState([]);
  const [medecinsHopitalDemandeur, setMedecinsHopitalDemandeur] = useState([]);
  const [medecinsHopitalDestination, setMedecinsHopitalDestination] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("apercu");
  const [form, setForm] = useState({
    motif: "",
    observation: "",
    circonstances: "",
    gestes: "",
    danger: "",
    ville: "",
    victimes: "",
    note: "",
    transportType: "",
    cause: "",
    urgence: "",
    statut: "",
    adresse: "",
    hospital_id: "",
    service_a: "",
    doc_a: "",
    adresse_destination: "",
    hospital_destination_id: "",
    service_b: "",
    doc_b: "",
    accordDestination: "",
    ville_dest: "",
    ta: "",
    fc: "",
    spo2: "",
    glasgow: "",
    respire: "",
    conscient: "",
    autre: "",
    interrogatoire: "",
    hypothese: "",
    conduite: "",
    contre_indications: "",
    decision: "",
    motif_de_cloture: "",
  });

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const missionFieldProps = {
    h: "42px",
    borderRadius: "6px",
    border: "1px solid",
    borderColor: "#E5E5E5",
    boxShadow: "0px 2px 3px 0px rgba(0,0,0,0.04)",
    fontSize: "13px",
    bg: "white",
  };

  const normalizeSexeForDisplay = (sexe) => {
    if (!sexe || typeof sexe !== "string") return "";
    const s = sexe.trim().toLowerCase();
    if (s === "m" || s === "male" || s === "homme" || s === "h") return "Homme";
    if (s === "f" || s === "female" || s === "femme") return "Femme";
    return sexe;
  };

  useEffect(() => {
    const fetchPatientsList = async () => {
      try {
        const res = await getAllPatients();
        const data = res?.data?.data ?? res?.data ?? [];
        setPatients(Array.isArray(data) ? data : []);
      } catch {
        setPatients([]);
      }
    };
    fetchPatientsList();
  }, []);

  useEffect(() => {
    const source = patientById || dossier?.patient || null;
    if (!source) return;
    setPatientForm({
      ipp: source.ipp != null ? String(source.ipp) : "",
      nom: source.nom || "",
      prenom: source.prenom || "",
      age: source.age != null ? String(source.age) : "",
      sexe: normalizeSexeForDisplay(source.sexe) || "",
      téléphone: source.téléphone ?? source.telephone ?? "",
      cin: source.cin || "",
      adresse: source.adresse || "",
      ville: source.ville || "",
    });
  }, [patientById, dossier?.patient]);

  useEffect(() => {
    const fetchHopitaux = async () => {
      try {
        const res = await apiClient.get(`${BASE_URL}/hopitals`);
        setHospitals(Array.isArray(res.data) ? res.data : []);
      } catch {
        setHospitals([]);
      }
    };

    const fetchHospitalVilles = async () => {
      try {
        const res = await apiClient.get(`${BASE_URL}/hopitals/villes`);
        setHospitalVilles(Array.isArray(res.data) ? res.data : []);
      } catch {
        setHospitalVilles([]);
      }
    };

    fetchHopitaux();
    fetchHospitalVilles();
  }, []);

  const fetchMedecinsByHopital = async (hospitalId) => {
    if (!hospitalId) return [];
    try {
      const res = await apiClient.get(`${BASE_URL}/users/hopital/${hospitalId}/medecins`);
      return Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (!form.hospital_id) {
      setMedecinsHopitalDemandeur([]);
      return;
    }
    fetchMedecinsByHopital(form.hospital_id).then((list) => setMedecinsHopitalDemandeur(list));
  }, [form.hospital_id]);

  useEffect(() => {
    if (!form.hospital_destination_id) {
      setMedecinsHopitalDestination([]);
      return;
    }
    fetchMedecinsByHopital(form.hospital_destination_id).then((list) => setMedecinsHopitalDestination(list));
  }, [form.hospital_destination_id]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const dossierRes = await apiClient.get(`${BASE_URL}/dossier/${id}`);
        const dossierData = dossierRes.data || null;
        setDossier(dossierData);

        if (!dossierData) return;

        const userIds = [
          ...new Set([dossierData.créé_par, dossierData.assigne, dossierData.doc_a, dossierData.doc_b].filter(Boolean)),
        ];

        const requests = [];

        if (dossierData.patient_id) {
          requests.push(
            apiClient
              .get(`${BASE_URL}/patients/${dossierData.patient_id}`)
              .then((res) => setPatientById(res.data || null))
              .catch(() => setPatientById(null))
          );
        } else {
          setPatientById(null);
        }

        if (userIds.length > 0) {
          requests.push(
            Promise.all(userIds.map((userId) => apiClient.get(`${BASE_URL}/users/${userId}`)))
              .then((responses) => {
                const nextMap = {};
                responses.forEach((res, idx) => {
                  const userId = userIds[idx];
                  nextMap[String(userId)] = res.data || null;
                });
                setUsersById(nextMap);
              })
              .catch(() => setUsersById({}))
          );
        } else {
          setUsersById({});
        }

        setForm({
          motif: dossierData.motif || "",
          observation: dossierData.observation || "",
          circonstances: dossierData.circonstances || "",
          gestes: dossierData.gestes || "",
          danger: dossierData.danger || "",
          ville: dossierData.ville || "",
          victimes: dossierData.victimes != null ? String(dossierData.victimes) : "",
          note: dossierData.note || "",
          transportType: dossierData.transportType || "",
          cause: dossierData.cause || "",
          urgence: dossierData.urgence != null ? String(dossierData.urgence) : "",
          statut: dossierData.statut || "",
          adresse: dossierData.adresse || "",
          hospital_id: dossierData.hospital_id != null ? String(dossierData.hospital_id) : "",
          service_a: dossierData.service_a || "",
          doc_a: dossierData.doc_a != null ? String(dossierData.doc_a) : "",
          adresse_destination: dossierData.adresse_destination || "",
          hospital_destination_id: dossierData.hospital_destination_id != null ? String(dossierData.hospital_destination_id) : "",
          service_b: dossierData.service_b || "",
          doc_b: dossierData.doc_b != null ? String(dossierData.doc_b) : "",
          accordDestination:
            dossierData.accord === 1 ||
            dossierData.accord === true ||
            dossierData.accord === "1" ||
            String(dossierData.accord || "").toLowerCase() === "oui"
              ? "oui"
              : dossierData.accord === 0 ||
                  dossierData.accord === false ||
                  dossierData.accord === "0" ||
                  String(dossierData.accord || "").toLowerCase() === "non"
                ? "non"
                : "",
          ville_dest: dossierData.ville_dest || "",
          ta: dossierData.ta || "",
          fc: dossierData.fc || "",
          spo2: dossierData.spo2 || "",
          glasgow: dossierData.glasgow || "",
          respire:
            dossierData.respire === 1 || dossierData.respire === "1" || String(dossierData.respire).toLowerCase() === "oui"
              ? "oui"
              : dossierData.respire === 0 || dossierData.respire === "0" || String(dossierData.respire).toLowerCase() === "non"
                ? "non"
                : "",
          conscient:
            dossierData.conscient === 1 || dossierData.conscient === "1" || String(dossierData.conscient).toLowerCase() === "oui"
              ? "oui"
              : dossierData.conscient === 0 || dossierData.conscient === "0" || String(dossierData.conscient).toLowerCase() === "non"
                ? "non"
                : "",
          autre: dossierData.autre || "",
          interrogatoire: dossierData.interrogatoire || "",
          hypothese: dossierData.hypothese || "",
          conduite: dossierData.conduite || "",
          contre_indications: dossierData.contre_indications || "",
          decision: dossierData.decision || "",
          motif_de_cloture: dossierData.motif_de_cloture || "",
        });

        if (requests.length > 0) await Promise.all(requests);
      } catch (e) {
        setError("Impossible de charger les détails du dossier.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetails();
  }, [id]);

  const formatSexe = (sexe) => {
    const s = String(sexe || "").trim().toLowerCase();
    if (["m", "male", "homme", "h"].includes(s)) return "Homme";
    if (["f", "female", "femme"].includes(s)) return "Femme";
    return sexe || "---";
  };

  const formatDate = (value) => {
    if (!value) return "---";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("fr-FR");
  };

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "---";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "---";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const getInputValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const getUserFullName = (userId, fallbackName = "Utilisateur") => {
    if (!userId) return "---";
    const user = usersById[String(userId)];
    if (user) {
      const fullName = `${user.nom || ""} ${user.prenom || ""}`.trim();
      if (fullName) return fullName;
    }
    return `${fallbackName} #${userId}`;
  };

  const getStatusBg = (statut) => {
    const value = String(statut || "").toLowerCase();
    if (value.includes("attente")) return "#F6B100";
    if (value.includes("mission")) return "#459CE9";
    if (value.includes("term")) return "#29BACD";
    return "#656C7A";
  };

  const patient = patientById || dossier?.patient || null;
  const isCallerPatient =
    dossier?.appelantEstPatient === 1 ||
    dossier?.appelantEstPatient === "1" ||
    dossier?.appelantEstPatient === true;

  const patientName = useMemo(() => {
    const fullName = `${patientForm.nom || patient?.nom || ""} ${patientForm.prenom || patient?.prenom || ""}`.trim();
    if (fullName) return fullName;
    if (dossier?.patient_id) return `Patient #${dossier.patient_id}`;
    return "---";
  }, [patientForm.nom, patientForm.prenom, patient, dossier?.patient_id]);

  const handlePatientChange = (field, value) => {
    const normalizedValue =
      field === "téléphone" && typeof value === "string"
        ? value.replace(/[^0-9]/g, "").slice(0, 10)
        : value;
    setPatientForm((prev) => ({ ...prev, [field]: normalizedValue }));
  };

  const handlePatientIppChange = (value) => {
    const ippTrim = String(value ?? "").trim();
    const match = ippTrim
      ? patients.find((p) => String(p.ipp ?? "").trim() === ippTrim)
      : null;

    if (match) {
      setPatientForm({
        ipp: ippTrim,
        nom: match.nom ?? "",
        prenom: match.prenom ?? "",
        age: match.age != null ? String(match.age) : "",
        sexe: normalizeSexeForDisplay(match.sexe) || "",
        téléphone: match.téléphone ?? match.telephone ?? "",
        cin: match.cin ?? "",
        adresse: match.adresse ?? "",
        ville: match.ville ?? "",
      });
      return;
    }

    setPatientForm((prev) => ({
      ...prev,
      ipp: value,
      nom: "",
      prenom: "",
      age: "",
      sexe: "",
      téléphone: "",
      cin: "",
      adresse: "",
      ville: "",
    }));
  };

  const isSecondaire = useMemo(
    () => String(form.transportType || "").trim().toLowerCase() === "secondaire",
    [form.transportType]
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toNull = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str === "" ? null : value;
  };

  const toOuiNonInt = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "oui") return 1;
    if (normalized === "non") return 0;
    return null;
  };

  const stripDossierNestedData = (data) => {
    if (!data || typeof data !== "object") return {};
    const {
      patient,
      mission,
      medecin,
      ambulancier,
      hopital,
      hopital_destination,
      created_by_full_name,
      ...dossierFields
    } = data;
    return dossierFields;
  };

  const buildVisibleDossierUpdates = () => ({
    motif: toNull(form.motif),
    observation: toNull(form.observation),
    circonstances: isSecondaire ? null : toNull(form.circonstances),
    gestes: isSecondaire ? null : toNull(form.gestes),
    danger: isSecondaire ? null : toNull(form.danger),
    ville: toNull(form.ville),
    victimes: isSecondaire ? null : toNull(form.victimes ? Number(form.victimes) : null),
    note: toNull(form.note),
    transportType: toNull(form.transportType),
    cause: toNull(form.cause),
    urgence: toNull(form.urgence ? Number(form.urgence) : null),
    statut: toNull(form.statut),
    adresse: toNull(form.adresse),
    hospital_id: toNull(form.hospital_id ? Number(form.hospital_id) : null),
    service_a: toNull(form.service_a),
    doc_a: toNull(form.doc_a ? Number(form.doc_a) : null),
    adresse_destination: toNull(form.adresse_destination),
    hospital_destination_id: toNull(form.hospital_destination_id ? Number(form.hospital_destination_id) : null),
    service_b: toNull(form.service_b),
    doc_b:
      form.accordDestination === "oui" && form.doc_b
        ? toNull(Number(form.doc_b))
        : null,
    ville_dest: toNull(form.ville_dest),
    ta: toNull(form.ta),
    fc: toNull(form.fc),
    spo2: toNull(form.spo2),
    glasgow: toNull(form.glasgow),
    respire: isSecondaire ? null : toOuiNonInt(form.respire),
    conscient: isSecondaire ? null : toOuiNonInt(form.conscient),
    autre: toNull(form.autre),
    interrogatoire: toNull(form.interrogatoire),
    hypothese: toNull(form.hypothese),
    conduite: toNull(form.conduite),
    contre_indications: toNull(form.contre_indications),
    decision: toNull(form.decision),
    motif_de_cloture: toNull(form.motif_de_cloture),
    accord: toOuiNonInt(form.accordDestination),
  });

  const savePatient = async () => {
    const patientId = dossier?.patient_id;
    if (!patientId) return;

    const updatePatientData = {
      ipp: toNull(patientForm.ipp),
      nom: toNull(patientForm.nom),
      prenom: toNull(patientForm.prenom),
      age: toNull(patientForm.age),
      sexe: toNull(patientForm.sexe),
      téléphone: toNull(patientForm.téléphone),
      cin: toNull(patientForm.cin),
      adresse: toNull(patientForm.adresse),
      ville: toNull(patientForm.ville),
    };

    await apiClient.put(`${BASE_URL}/patients/${patientId}`, updatePatientData);
    const freshRes = await apiClient.get(`${BASE_URL}/patients/${patientId}`);
    setPatientById(freshRes.data || null);
  };

  const save = async () => {
    try {
      setSaving(true);

      if (dossier?.patient_id) {
        if (!String(patientForm.nom || "").trim() || !String(patientForm.prenom || "").trim()) {
          toast({
            title: "Patient incomplet",
            description: "Le nom et le prénom du patient sont obligatoires.",
            status: "warning",
            isClosable: true,
          });
          setSaving(false);
          return;
        }
        try {
          await savePatient();
        } catch (patientErr) {
          toast({
            title: "Erreur patient",
            description: "Impossible de mettre à jour les informations du patient.",
            status: "error",
            isClosable: true,
          });
          setSaving(false);
          return;
        }
      }

      const visibleUpdates = buildVisibleDossierUpdates();
      const payload = {
        ...stripDossierNestedData(dossier),
        ...visibleUpdates,
      };

      try {
        await apiClient.put(`${BASE_URL}/dossier/${id}`, payload);
      } catch {
        await apiClient.patch(`${BASE_URL}/dossier/${id}`, payload);
      }

      toast({
        title: "Dossier mis à jour",
        status: "success",
        isClosable: true,
      });
      navigate(`/admin/dossier-regulation/${id}`);
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le dossier.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
        <Flex align="center" justify="center" minH="280px">
          <Spinner />
        </Flex>
      </Box>
    );
  }

  if (error || !dossier) {
    return (
      <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
        <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
          <Text color="red.500" mb="16px">{error || "Dossier introuvable."}</Text>
          <Button variant="outline" onClick={() => navigate("/admin/dossier-regulation")}>
            Retour liste
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
      <Box mb="20px">
        <HStack spacing="10px" mb="20px" align="center">
          <IconButton
            icon={<Icon as={MdArrowBack} />}
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/dossier-regulation")}
            aria-label="Retour"
            w="36px"
            h="36px"
            borderRadius="6px"
            _hover={{ bg: "gray.100" }}
          />
          <Text fontSize="14px" color="#656C7A" cursor="pointer" _hover={{ color: "#455879" }} onClick={() => navigate("/admin/dossier-regulation")}>
            Dossier de régulation
          </Text>
          <Icon as={MdArrowForward} w="12px" h="12px" color="#656C7A" transform="rotate(-90deg)" />
          <Text fontSize="14px" color="#455879" fontWeight="600">Modifier le dossier</Text>
        </HStack>

        <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
          <Flex justify="space-between" align="flex-start" mb="14px" wrap="wrap" gap="14px">
            <HStack spacing="20px">
              <Box w="72px" h="72px" borderRadius="6px" bg="#E3F2FD" display="flex" alignItems="center" justifyContent="center">
                <Icon as={MdInfo} w="36px" h="36px" color="#459CE9" />
              </Box>
              <VStack align="flex-start" spacing="4px">
                <Text fontSize="20px" fontWeight="600" color="#455879">
                  Réf. Dossier DR-{String(dossier.id || "").padStart(4, "0")}
                </Text>
                <HStack spacing="20px" wrap="wrap">
                  <HStack spacing="5px">
                    <Icon as={MdCalendarToday} w="16px" h="16px" color="#656C7A" />
                    <Text fontSize="14px" color="#656C7A">Créé le: {formatDate(dossier.créé_le || dossier.created_at)}</Text>
                  </HStack>
                  <HStack spacing="5px">
                    <Avatar size="sm" name={dossier.créé_par ? getUserFullName(dossier.créé_par) : "ARM"} />
                    <Text fontSize="14px" color="#455879">Créé par: {dossier.créé_par ? getUserFullName(dossier.créé_par) : displayValue(dossier.created_by_full_name)}</Text>
                  </HStack>
                  <HStack spacing="5px">
                    <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                    <Text fontSize="14px" color="#455879">Timer: {displayValue(dossier.timer)}</Text>
                  </HStack>
                </HStack>
              </VStack>
            </HStack>

            <HStack spacing="10px">
              <Badge bg={String(form.cause || "").toLowerCase().includes("u0") || String(form.cause || "").toLowerCase().includes("u1") ? "#F8285A" : "#F6B100"} color="white" borderRadius="20px" px="10px" py="6px" fontSize="12px">
                {displayValue(form.cause).toUpperCase()}
              </Badge>
              <Badge bg={getStatusBg(form.statut)} color="white" borderRadius="20px" px="10px" py="6px" fontSize="12px">
                {displayValue(form.statut).toUpperCase()}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => navigate(`/admin/dossier-regulation/${id}`)}>
                Annuler
              </Button>
              <Button size="sm" onClick={save} isLoading={saving}>
                Sauvegarder
              </Button>
            </HStack>
          </Flex>

          <HStack spacing="0" borderTop="1px solid" borderColor={borderColor} pt="10px">
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "apercu" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("apercu")}
            >
              Aperçu du dossier
            </Button>
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "patient" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("patient")}
            >
              Informations patient
            </Button>
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "decision" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("decision")}
            >
              Décision
            </Button>
          </HStack>
        </Box>
      </Box>

      {activeTab === "decision" && (
        <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }} mb="20px">
          <Box flex="1" minW={{ base: "100%", xl: "880px" }}>
            <VStack spacing="16px" align="stretch">
              <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} overflow="hidden">
                <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="16px" fontWeight="600" color="#455879">Compléments cliniques - Bilan médecin</Text>
                </Box>
                <Box p="16px">
                  <VStack align="stretch" spacing="14px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Interrogatoire médical</Text>
                      <Textarea value={form.interrogatoire} onChange={(e) => handleChange("interrogatoire", e.target.value)} minH="74px" />
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Hypothèse diagnostique</Text>
                      <Textarea value={form.hypothese} onChange={(e) => handleChange("hypothese", e.target.value)} minH="74px" />
                    </Box>
                  </VStack>
                </Box>
              </Box>

              <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} overflow="hidden">
                <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="16px" fontWeight="600" color="#455879">Conduite à tenir médicale</Text>
                </Box>
                <Box p="16px">
                  <VStack align="stretch" spacing="14px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Conduite à tenir</Text>
                      <Textarea value={form.conduite} onChange={(e) => handleChange("conduite", e.target.value)} minH="74px" />
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Contre-indications au transport</Text>
                      <Textarea value={form.contre_indications} onChange={(e) => handleChange("contre_indications", e.target.value)} minH="74px" />
                    </Box>
                  </VStack>
                </Box>
              </Box>
            </VStack>
          </Box>

          <Box w={{ base: "100%", xl: "520px" }} position={{ base: "static", xl: "sticky" }} top={{ xl: "220px" }}>
            <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} p="12px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="12px">Décision de régulation</Text>
              <VStack align="stretch" spacing="10px" mb="12px">
                {[
                  { id: "accepter", title: "SMUR — Service Mobile Urgence Réanimation", desc: "Équipe médicalisée (médecin ou infirmier anesthésiste)", icon: "🚑" },
                  { id: "avis", title: "Avis", desc: "Avis médical seul, sans transport", icon: "💬" },
                  { id: "refus", title: "Refus", desc: "Transport non indiqué", icon: "⛔" },
                ].map((option) => {
                  const selected = String(form.decision || "").trim().toLowerCase() === option.id;
                  return (
                    <Box
                      key={option.id}
                      border="1px solid"
                      borderColor={selected ? "#2B6CF0" : "gray.200"}
                      borderRadius="10px"
                      p="10px 12px"
                      bg={selected ? "#F5F9FF" : "white"}
                      cursor="pointer"
                      onClick={() => handleChange("decision", option.id)}
                    >
                      <HStack justify="space-between" align="flex-start" spacing="10px">
                        <HStack align="flex-start" spacing="10px">
                          <Box minW="40px" h="40px" borderRadius="8px" border="1px solid" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center" fontSize="18px">
                            {option.icon}
                          </Box>
                          <VStack align="flex-start" spacing="2px">
                            <Text fontSize="14px" fontWeight="700" color="#455879" lineHeight="1.2">{option.title}</Text>
                            <Text fontSize="13px" color="#656C7A" lineHeight="1.2">{option.desc}</Text>
                          </VStack>
                        </HStack>
                        <Circle size="18px" border="2px solid" borderColor={selected ? "#2B6CF0" : "gray.300"} bg={selected ? "#2B6CF0" : "transparent"}>
                          {selected ? <Circle size="7px" bg="white" /> : null}
                        </Circle>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>

              <Box>
                <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Motif de clôture</Text>
                <Textarea value={form.motif_de_cloture} onChange={(e) => handleChange("motif_de_cloture", e.target.value)} minH="90px" />
              </Box>
            </Box>
          </Box>
        </Flex>
      )}

      {activeTab === "patient" && (
        <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }}>
          <Box flex="1" minW={{ base: "100%", xl: "880px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
              <HStack spacing="8px" mb="6px">
                <Icon as={MdLocalHospital} w="20px" h="20px" color="#459CE9" />
                <Text fontSize="16px" fontWeight="600" color="#455879">
                  Informations patient
                </Text>
              </HStack>
              <Text fontSize="14px" color="#656C7A" mb="20px">
                Modifiez les données du patient lié à ce dossier. Les changements sont enregistrés avec le bouton Sauvegarder.
              </Text>

              {!dossier?.patient_id ? (
                <Box bg="#F9F9F9" border="1px solid" borderColor="gray.200" borderRadius="6px" p="16px">
                  <Text fontSize="14px" color="#656C7A">
                    Aucun patient n'est associé à ce dossier.
                  </Text>
                </Box>
              ) : (
                <VStack spacing="14px" align="stretch">
                  <FormControl>
                    <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                      IPP
                    </FormLabel>
                    <Input
                      placeholder="Saisissez l'IPP"
                      value={patientForm.ipp}
                      onChange={(e) => handlePatientIppChange(e.target.value)}
                      {...missionFieldProps}
                    />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                    <FormControl isRequired>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Nom
                      </FormLabel>
                      <Input
                        placeholder="Nom du patient"
                        value={patientForm.nom}
                        onChange={(e) => handlePatientChange("nom", e.target.value)}
                        {...missionFieldProps}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Prénom
                      </FormLabel>
                      <Input
                        placeholder="Prénom du patient"
                        value={patientForm.prenom}
                        onChange={(e) => handlePatientChange("prenom", e.target.value)}
                        {...missionFieldProps}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                    <FormControl>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Âge
                      </FormLabel>
                      <Input
                        type="number"
                        min={0}
                        placeholder="Âge"
                        value={patientForm.age}
                        onChange={(e) => handlePatientChange("age", e.target.value)}
                        {...missionFieldProps}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Sexe
                      </FormLabel>
                      <RadioGroup
                        value={normalizeSexeForDisplay(patientForm.sexe)}
                        onChange={(value) => handlePatientChange("sexe", value)}
                      >
                        <HStack
                          spacing="24px"
                          py="10px"
                          px="14px"
                          border="1px solid"
                          borderColor="#E5E5E5"
                          borderRadius="6px"
                          bg="white"
                          minH="42px"
                        >
                          <Radio value="Homme" colorScheme="blue" size="md">
                            <Text fontSize="13px">Homme</Text>
                          </Radio>
                          <Radio value="Femme" colorScheme="blue" size="md">
                            <Text fontSize="13px">Femme</Text>
                          </Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                      Téléphone
                    </FormLabel>
                    <Input
                      placeholder="Téléphone du patient"
                      value={patientForm.téléphone}
                      onChange={(e) => handlePatientChange("téléphone", e.target.value)}
                      {...missionFieldProps}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                      CIN (facultatif)
                    </FormLabel>
                    <Input
                      placeholder="CIN du patient"
                      value={patientForm.cin}
                      onChange={(e) => handlePatientChange("cin", e.target.value)}
                      {...missionFieldProps}
                    />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                    <FormControl>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Adresse
                      </FormLabel>
                      <Input
                        placeholder="Adresse"
                        value={patientForm.adresse}
                        onChange={(e) => handlePatientChange("adresse", e.target.value)}
                        {...missionFieldProps}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                        Ville
                      </FormLabel>
                      <Input
                        placeholder="Ville"
                        value={patientForm.ville}
                        onChange={(e) => handlePatientChange("ville", e.target.value)}
                        {...missionFieldProps}
                      />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              )}
            </Box>
          </Box>

          <Box w={{ base: "100%", xl: "300px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Aperçu patient
              </Text>
              <VStack spacing="16px" align="stretch">
                <HStack spacing="12px">
                  <Avatar size="md" name={patientName} bg="#006133" />
                  <VStack align="flex-start" spacing="2px">
                    <Text fontSize="16px" fontWeight="700" color="#455879">{patientName}</Text>
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="10px">
                      {formatSexe(patientForm.sexe || patient?.sexe)}{" "}
                      {patientForm.age || patient?.age ? `• ${patientForm.age || patient?.age} ans` : ""}
                    </Badge>
                  </VStack>
                </HStack>
                <Text fontSize="14px" color="#455879">IPP: {displayValue(patientForm.ipp || patient?.ipp)}</Text>
                <Text fontSize="14px" color="#455879">
                  Tél.: {displayValue(patientForm.téléphone || patient?.téléphone || patient?.telephone)}
                </Text>
                <Text fontSize="14px" color="#455879">Patient ID: {displayValue(dossier.patient_id)}</Text>
              </VStack>
            </Box>
          </Box>
        </Flex>
      )}

      {activeTab === "apercu" && (
        <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }}>
          <Box flex="1" minW={{ base: "100%", xl: "1040px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">Contexte de Régulation</Text>
              <Text fontSize="14px" color="#656C7A" mb="20px">Contexte clinique et notes complémentaires</Text>
              <VStack spacing="15px" align="stretch" mb="20px">
                <Box bg="#F9F9F9" border="1px solid" borderColor="gray.200" borderRadius="6px" p="16px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="700" textTransform="uppercase" mb="10px" letterSpacing="0.5px">
                    Contexte Clinique
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="10px">
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Motif de transfert*</Text>
                      <Input size="sm" {...missionFieldProps} value={form.motif} onChange={(e) => handleChange("motif", e.target.value)} />
                    </Box>
                    {isSecondaire && (
                      <Box gridColumn={{ base: "auto", md: "span 2", xl: "span 2" }}>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Observation brève</Text>
                        <Textarea
                          size="sm"
                          mt="4px"
                          minH="72px"
                          borderRadius="6px"
                          border="1px solid"
                          borderColor="#E5E5E5"
                          value={form.observation}
                          onChange={(e) => handleChange("observation", e.target.value)}
                          placeholder="Synthèse clinique courte..."
                        />
                      </Box>
                    )}
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Circonstances</Text>
                      <Input size="sm" {...missionFieldProps} value={form.circonstances} onChange={(e) => handleChange("circonstances", e.target.value)} />
                    </Box>
                    )}
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Gestes déjà effectués</Text>
                      <Input size="sm" {...missionFieldProps} value={form.gestes} onChange={(e) => handleChange("gestes", e.target.value)} />
                    </Box>
                    )}
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Danger sur les lieux</Text>
                      <StyledSearchDropdown
                        value={form.danger}
                        onChange={(next) => handleChange("danger", next)}
                        options={[
                          { value: "Oui", label: "Oui" },
                          { value: "Non", label: "Non" },
                        ]}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    )}
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Ville</Text>
                      <StyledSearchDropdown
                        value={form.ville}
                        onChange={(ville) => {
                          handleChange("ville", ville);
                          handleChange("hospital_id", "");
                          handleChange("doc_a", "");
                        }}
                        options={hospitalVilles.map((v) => ({ value: String(v), label: String(v) }))}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Victimes</Text>
                      <Input size="sm" {...missionFieldProps} type="number" value={form.victimes} onChange={(e) => handleChange("victimes", e.target.value)} />
                    </Box>
                    )}
                  </SimpleGrid>
                </Box>
                <Box bg="#F9F9F9" border="1px solid" borderColor="gray.200" borderRadius="6px" p="16px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="6px">
                    Notes complémentaires pour le médecin régulateur
                  </Text>
                  <Textarea size="sm" value={form.note} onChange={(e) => handleChange("note", e.target.value)} minH="90px" />
                </Box>
              </VStack>
            </Box>

            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Informations du dossier
              </Text>
              <VStack spacing="25px" align="stretch">
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Détails de régulation
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Type de transport</Text>
                      </HStack>
                      <StyledSearchDropdown
                        value={form.transportType}
                        onChange={(next) => handleChange("transportType", next)}
                        options={[
                          { value: "primaire", label: "primaire" },
                          { value: "secondaire", label: "secondaire" },
                        ]}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Niveau d'urgence</Text>
                      </HStack>
                      <StyledSearchDropdown
                        value={form.cause}
                        onChange={(next) => handleChange("cause", next)}
                        options={[
                          { value: "u0", label: "U0 — Réanimation immédiate" },
                          { value: "u1", label: "U1 — Urgence vitale" },
                          { value: "u2", label: "U2 — Urgence relative" },
                          { value: "u3", label: "U3 — Pas d'urgence identifiée" },
                          { value: "u4", label: "U4 — Réservation SMUR" },
                          { value: "u5", label: "U5 — Conseil médical" },
                        ]}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Urgence / État</Text>
                      </HStack>
                      <HStack spacing="10px">
                        <Input size="sm" {...missionFieldProps} value={form.urgence} onChange={(e) => handleChange("urgence", e.target.value)} placeholder="Urgence" />
                        <Input size="sm" {...missionFieldProps} value={form.statut} onChange={(e) => handleChange("statut", e.target.value)} placeholder="Statut" />
                      </HStack>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                  <HStack mb="10px" justify="space-between">
                    <HStack spacing="8px">
                      <Icon as={MdLocationOn} color="#2B6CF0" />
                      <Text fontWeight="600" fontSize="14px">
                        {form.transportType === "primaire" ? "Transport Primaire" : "Transport Secondaire"}
                      </Text>
                    </HStack>
                    <Icon as={MdExpandMore} color="gray.500" />
                  </HStack>

                  <HStack spacing="10px" align="center" mb="10px">
                    <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                      Origine
                    </Text>
                    <Divider />
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                    <Box>
                      <Text fontSize="12px" mb="6px">Ville mission</Text>
                      <StyledSearchDropdown
                        value={form.ville}
                        onChange={(ville) => {
                          handleChange("ville", ville);
                          handleChange("hospital_id", "");
                          handleChange("doc_a", "");
                        }}
                        options={hospitalVilles.map((v) => ({ value: String(v), label: String(v) }))}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <Text fontSize="12px" mb="6px">Hôpital / Adresse mission</Text>
                      <StyledSearchDropdown
                        value={form.hospital_id}
                        onChange={(hospitalId) => {
                          handleChange("hospital_id", hospitalId);
                          const selected = hospitals.find((h) => String(h.id) === String(hospitalId));
                          if (selected?.nom) handleChange("adresse", selected.nom);
                          if (selected?.ville) handleChange("ville", selected.ville);
                          handleChange("doc_a", "");
                        }}
                        options={hospitals
                          .filter((h) => !form.ville || String(h.ville || "").trim() === String(form.ville || "").trim())
                          .map((h) => ({ value: String(h.id), label: String(h.nom || `Hôpital #${h.id}`) }))}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <Text fontSize="12px" mb="6px">Service</Text>
                      <Input size="sm" {...missionFieldProps} placeholder="Saisissez le service..." value={form.service_a} onChange={(e) => handleChange("service_a", e.target.value)} />
                    </Box>
                    <Box>
                      <Text fontSize="12px" mb="6px">Médecin demandeur</Text>
                      <DoctorSearchDropdown
                        value={form.doc_a}
                        onChange={(doctorId) => handleChange("doc_a", doctorId)}
                        options={medecinsHopitalDemandeur}
                        placeholder="Recherche Dr."
                      />
                    </Box>
                  </SimpleGrid>

                  <HStack spacing="10px" align="center" mb="10px" mt="12px">
                    <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                      Destination
                    </Text>
                    <Divider />
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                    <Box>
                      <Text fontSize="12px" mb="6px">Ville destination</Text>
                      <StyledSearchDropdown
                        value={form.ville_dest}
                        onChange={(ville) => {
                          handleChange("ville_dest", ville);
                          handleChange("hospital_destination_id", "");
                          handleChange("doc_b", "");
                        }}
                        options={hospitalVilles.map((v) => ({ value: String(v), label: String(v) }))}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <Text fontSize="12px" mb="6px">Hôpital / Adresse destination</Text>
                      <StyledSearchDropdown
                        value={form.hospital_destination_id}
                        onChange={(hospitalId) => {
                          handleChange("hospital_destination_id", hospitalId);
                          const selected = hospitals.find((h) => String(h.id) === String(hospitalId));
                          if (selected?.nom) handleChange("adresse_destination", selected.nom);
                          if (selected?.ville) handleChange("ville_dest", selected.ville);
                          handleChange("doc_b", "");
                        }}
                        options={hospitals
                          .filter((h) => !form.ville_dest || String(h.ville || "").trim() === String(form.ville_dest || "").trim())
                          .map((h) => ({ value: String(h.id), label: String(h.nom || `Hôpital #${h.id}`) }))}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    <Box>
                      <Text fontSize="12px" mb="6px">Service destination</Text>
                      <Input size="sm" {...missionFieldProps} placeholder="Saisissez le service destination..." value={form.service_b} onChange={(e) => handleChange("service_b", e.target.value)} />
                    </Box>
                  </SimpleGrid>

                  <Box mt="12px">
                    <Text fontSize="12px" color="#656C7A" fontWeight="600" mb="6px">Accord obtenu</Text>
                    <RadioGroup
                      value={form.accordDestination}
                      onChange={(v) => {
                        handleChange("accordDestination", v);
                        if (v !== "oui") handleChange("doc_b", "");
                      }}
                    >
                      <HStack spacing="24px">
                        <Radio value="oui">Oui</Radio>
                        <Radio value="non">Non</Radio>
                      </HStack>
                    </RadioGroup>
                  </Box>

                  {form.accordDestination === "oui" && (
                    <Box mt="10px">
                      <Text fontSize="12px" mb="6px">Médecin receveur</Text>
                      <DoctorSearchDropdown
                        value={form.doc_b}
                        onChange={(doctorId) => handleChange("doc_b", doctorId)}
                        options={medecinsHopitalDestination}
                        placeholder="Recherche Dr."
                      />
                    </Box>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Constantes et contexte
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing="20px">
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">TA</Text><Input size="sm" {...missionFieldProps} value={form.ta} onChange={(e) => handleChange("ta", e.target.value)} /></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">FC</Text><Input size="sm" {...missionFieldProps} value={form.fc} onChange={(e) => handleChange("fc", e.target.value)} /></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">SpO2</Text><Input size="sm" {...missionFieldProps} value={form.spo2} onChange={(e) => handleChange("spo2", e.target.value)} /></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">Glasgow</Text><Input size="sm" {...missionFieldProps} value={form.glasgow} onChange={(e) => handleChange("glasgow", e.target.value)} /></Box>
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500">Respire</Text>
                      <StyledSearchDropdown
                        value={form.respire}
                        onChange={(next) => handleChange("respire", next)}
                        options={[
                          { value: "oui", label: "Oui" },
                          { value: "non", label: "Non" },
                        ]}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    )}
                    {!isSecondaire && (
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500">Conscient</Text>
                      <StyledSearchDropdown
                        value={form.conscient}
                        onChange={(next) => handleChange("conscient", next)}
                        options={[
                          { value: "oui", label: "Oui" },
                          { value: "non", label: "Non" },
                        ]}
                        placeholder="Sélectionner..."
                      />
                    </Box>
                    )}
                    <Box gridColumn={{ base: "auto", md: "span 2" }}>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500">Autres constantes / Remarques</Text>
                      <Textarea size="sm" value={form.autre} onChange={(e) => handleChange("autre", e.target.value)} minH="72px" />
                    </Box>
                  </SimpleGrid>
                </Box>
              </VStack>
            </Box>
          </Box>

          <Box w={{ base: "100%", xl: "300px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Flex justify="space-between" align="center" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                <Text fontSize="16px" fontWeight="600" color="#455879">
                  Patient
                </Text>
                {dossier?.patient_id ? (
                  <Button size="xs" variant="link" color="#459CE9" onClick={() => setActiveTab("patient")}>
                    Modifier
                  </Button>
                ) : null}
              </Flex>
              <VStack spacing="16px" align="stretch">
                <HStack spacing="12px">
                  <Avatar size="md" name={patientName} bg="#006133" />
                  <VStack align="flex-start" spacing="2px">
                    <Text fontSize="16px" fontWeight="700" color="#455879">{patientName}</Text>
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="10px">
                      {formatSexe(patientForm.sexe || patient?.sexe)}{" "}
                      {patientForm.age || patient?.age ? `• ${patientForm.age || patient?.age} ans` : ""}
                    </Badge>
                  </VStack>
                </HStack>
                <HStack spacing="6px">
                  <Icon as={MdPerson} w="15px" h="15px" color="#656C7A" />
                  <Text fontSize="14px" color="#455879">IPP: {displayValue(patientForm.ipp || patient?.ipp || dossier.ipp)}</Text>
                </HStack>
                <HStack spacing="6px">
                  <Icon as={MdPhone} w="15px" h="15px" color="#656C7A" />
                  <Text fontSize="14px" color="#455879">
                    {displayValue(patientForm.téléphone || patient?.téléphone || patient?.telephone || dossier.phone)}
                  </Text>
                </HStack>
                <Text fontSize="14px" color="#455879">Patient ID: {displayValue(dossier.patient_id)}</Text>
                {!isCallerPatient && (
                  <Box mt="8px" pt="10px" borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="8px" letterSpacing="0.5px">
                      Appelant
                    </Text>
                    <VStack align="stretch" spacing="6px">
                      <Text fontSize="14px" color="#455879">Nom: {displayValue(dossier.nomAppelant)}</Text>
                      <Text fontSize="14px" color="#455879">Relation: {displayValue(dossier.relationAppelant)}</Text>
                      <Text fontSize="14px" color="#455879">Téléphone: {displayValue(dossier.telephoneAppelant)}</Text>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>

            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">L'équipe assignée</Text>
              <VStack align="stretch" spacing="16px">
                <HStack spacing="10px">
                  <Avatar size="sm" name={dossier.assigne ? getUserFullName(dossier.assigne) : "Assigné"} bg="#3182CE" />
                  <VStack align="flex-start" spacing="5px" flex="1">
                    <Text fontSize="14px" fontWeight="600" color="#455879">
                      {dossier.assigne ? getUserFullName(dossier.assigne) : "---"}
                    </Text>
                    <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                      ASSIGNÉ
                    </Badge>
                  </VStack>
                </HStack>

                <HStack spacing="10px">
                  <Avatar size="sm" name={dossier.créé_par ? getUserFullName(dossier.créé_par) : "ARM"} bg="#6B46C1" />
                  <VStack align="flex-start" spacing="5px" flex="1">
                    <Text fontSize="14px" fontWeight="600" color="#455879">
                      {dossier.créé_par ? getUserFullName(dossier.créé_par) : "---"}
                    </Text>
                    <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                      ARM CREATEUR
                    </Badge>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </Box>
        </Flex>
      )}
    </Box>
  );
}